import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import Loading from './components/common/Loading';
import ProtectedRoute from './routes/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';

// Lazy-loaded Pages
const ProductList = lazy(() => import('./pages/ProductList'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const NoticeBoard = lazy(() => import('./pages/NoticeBoard'));
const NoticeDetail = lazy(() => import('./pages/NoticeDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Lazy-loaded Admin Pages
const Login = lazy(() => import('./pages/admin/Login'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ProductManagement = lazy(() => import('./pages/admin/ProductManagement'));
const ProductEdit = lazy(() => import('./pages/admin/ProductEdit'));
const NoticeManagement = lazy(() => import('./pages/admin/NoticeManagement'));
const NoticeEdit = lazy(() => import('./pages/admin/NoticeEdit'));
const Statistics = lazy(() => import('./pages/admin/Statistics'));

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <div className="min-h-screen flex flex-col">
              <Header />
              <main className="flex-grow">
                <Suspense fallback={<Loading />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<ProductList />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/notice" element={<NoticeBoard />} />
                    <Route path="/notice/:id" element={<NoticeDetail />} />

                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<Login />} />
                    <Route
                      path="/admin/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/products"
                      element={
                        <ProtectedRoute>
                          <ProductManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/products/new"
                      element={
                        <ProtectedRoute>
                          <ProductEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/products/:id/edit"
                      element={
                        <ProtectedRoute>
                          <ProductEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/notices"
                      element={
                        <ProtectedRoute>
                          <NoticeManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/notices/new"
                      element={
                        <ProtectedRoute>
                          <NoticeEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/notices/:id/edit"
                      element={
                        <ProtectedRoute>
                          <NoticeEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/stats"
                      element={
                        <ProtectedRoute>
                          <Statistics />
                        </ProtectedRoute>
                      }
                    />

                    {/* 404 Route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
